import { useLazyQuery } from "@apollo/client";
import { PlayIcon, UserIcon, VideoCameraSlashIcon } from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaCard,
  MediaCornerActions,
  MediaFrame,
  MediaGrid,
  MediaIconChip,
  MediaImage,
} from "../../components/Styled/Media";
import { PagingResult, Video } from "../../graphql/graphql";
import { PRIVATE_VIDEOS } from "../../graphql/queries-gql";

const PAGE_SIZE = 20;

const PlayChip = styled(MediaIconChip)`
  position: absolute;
  right: 8px;
  bottom: 8px;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const PrivateVideos = () => {
  const [fetchVideos, { loading, error }] = useLazyQuery(PRIVATE_VIDEOS);
  const [page, setPage] = useState<PagingResult | undefined>(undefined);
  const [videos, setVideos] = useState<Video[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!error && !page) {
      fetchVideos({
        variables: {
          paging: {
            type: "page",
            pageArgs: {
              page: 1,
              limit: PAGE_SIZE,
            },
          },
        },
        onCompleted: (data) => {
          console.log("fetchVideos onCompleted", data);
          setPage(data.privateVideos.paging);
          setVideos(data.privateVideos.nodes);
        },
        onError: (error) => {
          console.log("fetchVideos onError", error);
        },
      });
    }
  }, [error, fetchVideos, page]);

  const openUrl = (src: string) => {
    window.open(src, "_blank");
  };

  const showMore = () => {
    fetchVideos({
      variables: {
        paging: {
          type: "page",
          pageArgs: {
            page: page?.nextPage,
            limit: PAGE_SIZE,
          },
        },
      },
      onCompleted: (data) => {
        console.log("privateVideos onCompleted", data);
        setPage(data.privateVideos.paging);
        setVideos([...videos, ...data.privateVideos.nodes]);
      },
      onError: (error) => {
        console.log("privateVideos onError", error);
      },
    });
  };

  return (
    <Page
      title="Private videos"
      description="Videos users share privately. Click a video to open it in a new tab."
      badge={page?.total != null && <Badge>{page.total} total</Badge>}
    >
      {error && <ErrorAlert error={error} />}

      {page && !error && videos.length === 0 && (
        <EmptyState
          Icon={VideoCameraSlashIcon}
          title="No private videos"
          description="Nothing to review right now."
        />
      )}

      {videos && videos.length > 0 && (
        <MediaGrid $min={170} $gap={12}>
          {videos.map((video) => (
            <MediaCard key={video.uploadId}>
              <MediaFrame $ratio="1 / 1" onClick={() => openUrl(video.url)}>
                <MediaImage
                  src={video.thumbnailUrl ?? ""}
                  alt=""
                  loading="lazy"
                />
                <MediaCornerActions>
                  <ActionButton
                    label="Open user"
                    size={30}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/user/${video.uploadById}`);
                    }}
                  >
                    <UserIcon />
                  </ActionButton>
                </MediaCornerActions>
                <PlayChip>
                  <PlayIcon />
                </PlayChip>
              </MediaFrame>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}

      {page?.hasNextPage && !loading && (
        <Row justifyContent="center">
          <Button variant="outlined" color="inherit" onClick={() => showMore()}>
            Show more videos
          </Button>
        </Row>
      )}
    </Page>
  );
};
export default PrivateVideos;
